import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { COMMS_LIMITS } from "@/lib/comms-limits";
import {
  appendOlderPage,
  prependEarlierPage,
  refreshPagedList,
  type PagedList,
} from "@/lib/keyset-list";
import { apiFetch } from "@/services/http/api-fetch";

type ForumThread = {
  id: string;
  title: string;
  body: string;
  authorName: string;
  createdAt: string;
  lessonId?: string | null;
  lessonTitle?: string | null;
};

type ForumReply = {
  id: string;
  body: string;
  authorName: string;
  createdAt: string;
};

type Props = {
  courseId: string;
  /** Required for lesson variant; omitted for course community hub. */
  lessonId?: string;
  variant?: "lesson" | "community";
};

export function ForumPanel({ courseId, lessonId, variant = "lesson" }: Props) {
  const t = useTranslations("forum");
  const tRoot = useTranslations();
  const errorText = useCallback(
    (err: unknown, fallbackKey: string) =>
      apiErrorText(err, tRoot, fallbackKey, { namespace: "forum.errors" }),
    [tRoot],
  );
  const isCommunity = variant === "community";
  // COMMS-12: the server pages both lists; each keeps the cursor for its next (older) page.
  // R-17: the thread list remembers which filter it belongs to, so a page fetched for a lesson
  // the reader has left is dropped.
  const lessonQuery = isCommunity && !lessonId ? "all" : encodeURIComponent(lessonId ?? "");
  const listKey = `${courseId}|${lessonQuery}`;
  const listKeyRef = useRef(listKey);
  const [threadList, setThreadList] = useState<PagedList<ForumThread> & { key: string }>({
    key: listKey,
    rows: [],
    cursor: null,
  });
  const threads = threadList.key === listKey ? threadList.rows : [];
  const threadsCursor = threadList.key === listKey ? threadList.cursor : null;
  const [replyList, setReplyList] = useState<PagedList<ForumReply>>({ rows: [], cursor: null });
  const replies = replyList.rows;
  const repliesCursor = replyList.cursor;
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  // COMMS-11 sibling: a slow reply list for a thread the user already left must not render.
  const expandedRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");

  /**
   * The newest page of threads: it replaces the list for a new filter; after a post or reply it
   * is merged in, keeping older pages the reader loaded (R-17).
   */
  const loadThreads = useCallback(
    async (signal?: AbortSignal, mode: "replace" | "merge" = "replace") => {
      try {
        const res = await apiFetch(
          `/v1/courses/${courseId}/forum/threads?lessonId=${lessonQuery}`,
          { signal },
        );
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as {
          data?: { threads: ForumThread[]; nextCursor?: string | null };
        };
        if (listKeyRef.current !== listKey) return;
        const page = { rows: json.data?.threads ?? [], cursor: json.data?.nextCursor ?? null };
        setThreadList((current) =>
          mode === "merge" && current.key === listKey
            ? { key: listKey, ...refreshPagedList(current, page, "newestFirst") }
            : { key: listKey, ...page },
        );
        setError(null);
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setError(errorText(err, "forum.loadFailed"));
      }
    },
    [courseId, lessonQuery, listKey, errorText],
  );

  useEffect(() => {
    listKeyRef.current = listKey;
  }, [listKey]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    void (async () => {
      setLoading(true);
      await loadThreads(controller.signal);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [loadThreads]);

  async function loadMoreThreads() {
    const cursor = threadsCursor;
    const key = listKey;
    if (!cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await apiFetch(
        `/v1/courses/${courseId}/forum/threads?lessonId=${lessonQuery}&before=${encodeURIComponent(cursor)}`,
      );
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as {
        data?: { threads: ForumThread[]; nextCursor?: string | null };
      };
      // R-17: the reader switched lesson meanwhile — this page belongs to the old list.
      if (listKeyRef.current !== key) return;
      const older = json.data?.threads ?? [];
      setThreadList((current) =>
        current.key === key
          ? {
              key,
              rows: appendOlderPage(current.rows, older),
              cursor: json.data?.nextCursor ?? null,
            }
          : current,
      );
    } catch (err) {
      if (listKeyRef.current === key) setError(errorText(err, "forum.loadFailed"));
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }

  /**
   * The newest page of replies, or (with `before`) the page before the ones shown. `refresh`
   * (after a reply) merges the newest page in, keeping earlier replies already loaded (R-17).
   */
  async function loadReplies(threadId: string, before?: string, refresh = false) {
    try {
      const query = before ? `?before=${encodeURIComponent(before)}` : "";
      const res = await apiFetch(`/v1/forum/threads/${threadId}${query}`);
      if (!res.ok) throw await readApiErrorResponse(res);
      const json = (await res.json()) as {
        data?: { replies: ForumReply[]; nextCursor?: string | null };
      };
      if (expandedRef.current !== threadId) return;
      const page = { rows: json.data?.replies ?? [], cursor: json.data?.nextCursor ?? null };
      setReplyList((current) => {
        if (before) {
          if (current.cursor !== before) return current;
          return { rows: prependEarlierPage(current.rows, page.rows), cursor: page.cursor };
        }
        return refresh ? refreshPagedList(current, page, "oldestFirst") : page;
      });
    } catch (err) {
      if (expandedRef.current !== threadId) return;
      setError(errorText(err, "forum.loadFailed"));
    }
  }

  async function handleCreateThread(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = isCommunity && !lessonId ? { title, body } : { title, body, lessonId };
      const res = await apiFetch(`/v1/courses/${courseId}/forum/threads`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setTitle("");
      setBody("");
      await loadThreads(undefined, "merge");
    } catch (err) {
      setError(errorText(err, "forum.createFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReply(threadId: string) {
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/v1/forum/threads/${threadId}/replies`, {
        method: "POST",
        body: JSON.stringify({ body: replyBody }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setReplyBody("");
      await loadReplies(threadId, undefined, true);
      await loadThreads(undefined, "merge");
    } catch (err) {
      setError(errorText(err, "forum.replyFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleThread(threadId: string) {
    setReplyList({ rows: [], cursor: null });
    if (expandedThreadId === threadId) {
      expandedRef.current = null;
      setExpandedThreadId(null);
      return;
    }
    expandedRef.current = threadId;
    setExpandedThreadId(threadId);
    await loadReplies(threadId);
  }

  return (
    <section
      className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-5"
      data-testid={isCommunity ? "course-community-panel" : "forum-panel"}
    >
      <h2 className="text-lg font-medium">{isCommunity ? t("communityTitle") : t("title")}</h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">
        {isCommunity ? t("communityHint") : t("lessonHint")}
      </p>

      {loading ? <p className="mt-4 text-sm text-[var(--text-muted)]">{t("loading")}</p> : null}
      {error ? (
        <p role="alert" className="mt-4 text-sm text-[var(--danger)]" data-testid="forum-error">
          {error}
        </p>
      ) : null}

      {!loading ? (
        <ul className="mt-4 space-y-3">
          {threads.map((thread) => (
            <li key={thread.id} className="rounded border border-[var(--border)] p-3">
              <button
                type="button"
                className="w-full text-left"
                onClick={() => void toggleThread(thread.id)}
                data-testid={`forum-thread-${thread.id}`}
              >
                <p className="font-medium">{thread.title}</p>
                {isCommunity ? (
                  <p
                    className="mt-1 text-xs uppercase tracking-wide text-[var(--text-muted)]"
                    data-testid={`forum-thread-lesson-${thread.id}`}
                  >
                    {thread.lessonTitle
                      ? t("lessonLabel", { title: thread.lessonTitle })
                      : t("courseWideLabel")}
                  </p>
                ) : null}
                <p className="mt-1 text-sm text-[var(--text-secondary)]">{thread.body}</p>
                <p className="mt-2 text-xs text-[var(--text-muted)]">
                  {t("postedBy", { name: thread.authorName })}
                </p>
              </button>

              {expandedThreadId === thread.id ? (
                <div className="mt-3 border-t border-[var(--border)] pt-3">
                  {repliesCursor ? (
                    <button
                      type="button"
                      className="mb-2 text-xs underline"
                      onClick={() => void loadReplies(thread.id, repliesCursor)}
                      data-testid={`forum-replies-earlier-${thread.id}`}
                    >
                      {t("loadEarlierReplies")}
                    </button>
                  ) : null}
                  <ul className="space-y-2" data-testid={`forum-replies-${thread.id}`}>
                    {replies.map((reply) => (
                      <li key={reply.id} className="text-sm">
                        <p>{reply.body}</p>
                        <p className="text-xs text-[var(--text-muted)]">
                          {t("postedBy", { name: reply.authorName })}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <form
                    className="mt-3 space-y-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleReply(thread.id);
                    }}
                  >
                    <textarea
                      className="w-full rounded border p-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                      rows={2}
                      value={replyBody}
                      onChange={(event) => setReplyBody(event.target.value)}
                      maxLength={COMMS_LIMITS.forumReply}
                      placeholder={t("replyPlaceholder")}
                      data-testid={`forum-reply-input-${thread.id}`}
                    />
                    <button
                      type="submit"
                      disabled={submitting || !replyBody.trim()}
                      className="rounded bg-[var(--ink-9)] px-3 py-1.5 text-sm text-white disabled:opacity-50"
                    >
                      {submitting ? t("working") : t("postReply")}
                    </button>
                  </form>
                </div>
              ) : null}
            </li>
          ))}
          {threads.length === 0 ? (
            <li className="text-sm text-[var(--text-muted)]">
              {isCommunity ? t("communityEmpty") : t("empty")}
            </li>
          ) : null}
        </ul>
      ) : null}
      {!loading && threadsCursor ? (
        <button
          type="button"
          className="mt-3 text-sm underline disabled:opacity-50"
          disabled={loadingMore}
          onClick={() => void loadMoreThreads()}
          data-testid="forum-threads-more"
        >
          {t("loadMoreThreads")}
        </button>
      ) : null}

      <form className="mt-6 space-y-3" onSubmit={(event) => void handleCreateThread(event)}>
        <h3 className="text-sm font-medium">{t("newThread")}</h3>
        <input
          className="w-full rounded border p-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={COMMS_LIMITS.forumThreadTitle}
          placeholder={t("threadTitlePlaceholder")}
          data-testid="forum-thread-title"
        />
        <textarea
          className="w-full rounded border p-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
          rows={3}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          maxLength={COMMS_LIMITS.forumThreadBody}
          placeholder={t("threadBodyPlaceholder")}
          data-testid="forum-thread-body"
        />
        <button
          type="submit"
          disabled={submitting || !title.trim() || !body.trim()}
          className="rounded bg-[var(--ink-9)] px-3 py-1.5 text-sm text-white disabled:opacity-50"
          data-testid="forum-create-thread"
        >
          {submitting ? t("working") : t("postThread")}
        </button>
      </form>
    </section>
  );
}
